import classNames from "classnames";
import { useEffect, useContext, useState, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import CnnTravelSvg from "assets/images/media/cnn-travel.svg";
import ForbesSvg from "assets/images/media/forbes.svg";
import MarketWatchSvg from "assets/images/media/market-watch.svg";
import TechCrunchSvg from "assets/images/media/tech-crunch.svg";
import TravelWeeklySvg from "assets/images/media/travel-weekly.svg";
import UsaTodaySvg from "assets/images/media/usa-today.svg";
import WallStreetJournalSvg from "assets/images/media/wall-street-journal.svg";
import { PlainButton } from "components/button/plain";
import { Form } from "components/form";
import { ScreenHeader } from "components/screen-header";
import { useDispatch } from "hooks/use-dispatch";
import { useSelector } from "hooks/use-selector";
import { OnboardingLayoutContext } from "layouts/onboarding";
import {
  selectDestinationsGoogleData,
  selectDestinations,
} from "store/quote/selectors";
import { setDestinationThunk } from "store/quote/thunks/steps";
import { isIsraelTrip } from "utils/google-places/destination/is-israel-trip";
import { getFeatureVariation } from "utils/experiments/cookie-features";
import { getGooglePlacesDestinationDetails } from "utils/google-places/destination/get-details";
import { getDestinationGooglePlacesSummary } from "utils/google-places/destination/get-summary";
import { type Destination } from "utils/google-places/destination";
import { Autocomplete } from "./autocomplete";
import { MediaBar } from "./media-bar";
import { Taglist, Tag, ClearIcon, DestinationErrorMessage } from "./styles";
import { trackSubmit, useTracking } from "./tracking";
import {
  type AutocompletePlace,
  FORM_NAME,
  isDomesticTrip,
  parseSelectedPlaces,
  selectedPlaceIdsAreEqual,
} from "./utils";
import { getGooglePlacesFromSearchParams } from "utils/google-places/get-from-search-params";
import { TalkingHead } from "components/talking-head";
import { listFormatter } from "utils/formatters/list";
import { mixpanelTrack } from "utils/tracking/mixpanel";

export function DestinationScreen() {
  const dispatch = useDispatch();
  const destinations = useSelector(selectDestinations);
  const destinationsGoogleData = useSelector(selectDestinationsGoogleData);
  const { showHeader } = useContext(OnboardingLayoutContext);

  const navigate = useNavigate();

  const { t } = useTranslation("screens", {
    keyPrefix: "destination",
  });

  const [shouldRedirect, setShouldRedirect] = useState(true);
  const [invalidDestinations, setInvalidDestinations] = useState<
    AutocompletePlace[]
  >([]);
  const [talkingHead, setTalkingHead] = useState({ show: false, text: "" });
  const [removedList, setRemovedList] = useState<string[] | null>(null);
  const [selectedPlaces, setSelectedPlaces] = useState<Destination[]>(() => {
    return destinations
      ? parseSelectedPlaces(destinations, destinationsGoogleData)
      : [];
  });

  const submittable =
    !!selectedPlaces.length && !selectedPlaces.some((place) => place.error);

  async function handleFormSubmit(places?: Destination[]) {
    const currentSelectedPlaces = places ?? selectedPlaces;

    if (!currentSelectedPlaces.length) {
      return;
    }

    const googlePlaces = currentSelectedPlaces.map(
      ({ placeId, ...destination }) => ({
        ...destination,
        googleId: placeId,
      }),
    );

    if (
      destinations &&
      destinationsGoogleData &&
      selectedPlaceIdsAreEqual(
        destinations.map((destination) => destination?.googleId ?? null),
        currentSelectedPlaces.map(({ placeId }) => placeId),
      )
    ) {
      await dispatch(
        setDestinationThunk({ destinationsGoogleData, destinations }),
      );

      trackSubmit(destinations);
    } else {
      const destinationsGoogleData = await Promise.all(
        googlePlaces.map(getGooglePlacesDestinationDetails),
      );

      const destinations = destinationsGoogleData.map((destination, index) =>
        getDestinationGooglePlacesSummary(destination, googlePlaces[index]),
      );

      await dispatch(
        setDestinationThunk({
          destinations,
          destinationsGoogleData,
        }),
      );

      trackSubmit(destinations);
    }

    navigate("/dates");
  }

  useEffect(() => {
    if (isIsraelTrip(selectedPlaces)) {
      setTalkingHead({
        show: true,
        text: t("talkingHead.israelTrip"),
      });
      return;
    } else if (selectedPlaces.length < 5) {
      setTalkingHead((talkingHead) => ({
        ...talkingHead,
        show: false,
      }));
      return;
    }

    setTalkingHead({
      show: true,
      text: isDomesticTrip(selectedPlaces)
        ? t("talkingHead.domestic")
        : t("talkingHead.international"),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlaces.length]);

  useEffect(() => {
    showHeader(true);
    return () => {
      showHeader(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Using a layout effect to avoid rendering before redirecting
  useLayoutEffect(() => {
    async function redirectWhenDestInParams() {
      const dest = await getGooglePlacesFromSearchParams();

      const [destinations, invalidDestinations] = dest.reduce<
        [AutocompletePlace[], AutocompletePlace[]]
      >(
        (acc, destination) => {
          const invalid = destination.disabled || destination.error;
          acc[invalid ? 1 : 0].push(destination);
          return acc;
        },
        [[], []],
      );

      if (destinations.length) {
        setSelectedPlaces(destinations);
      } else {
        setShouldRedirect(false);
      }

      if (
        invalidDestinations.length ||
        destinations.some((dest) => dest.warning)
      ) {
        setInvalidDestinations(invalidDestinations);
        setShouldRedirect(false);
      } else {
        void handleFormSubmit(dest);
      }
    }

    void redirectWhenDestInParams();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useTracking({ ctaEnabled: submittable, formName: FORM_NAME });

  return (
    !shouldRedirect && (
      <>
        <ScreenHeader
          caption={t("captionFemale")}
          title={t("title")}
          lead={t("lead")}
        />

        <Form
          name={FORM_NAME}
          onSubmit={async () => {
            void handleFormSubmit();
          }}
          SubmitButtonProps={{ showArrow: true }}
          submittable={submittable}
        >
          <Autocomplete
            name="destinations"
            selectedPlaces={selectedPlaces}
            onChange={(value) => {
              setSelectedPlaces([...selectedPlaces, value]);
            }}
            {...(isMobile && {
              autoFocus: true,
              onFocus() {
                showHeader(false, true);
              },
              onBlur() {
                showHeader(true, true);
              },
            })}
          />

          <Taglist>
            {selectedPlaces.map((place) => (
              <Tag
                key={place.placeId}
                className={classNames({
                  removed: removedList?.includes(place.placeId),
                })}
                onTransitionEnd={(event) => {
                  if (event.propertyName === "transform") {
                    const places = selectedPlaces.filter(
                      (item) => item.placeId !== place.placeId,
                    );
                    setSelectedPlaces(places);
                    setRemovedList(
                      (prev) =>
                        prev?.filter((id) => id !== place.placeId) ?? null,
                    );
                  }
                }}
              >
                {place.label}
                <PlainButton
                  onClick={() => {
                    setRemovedList((prev) => [...(prev ?? []), place.placeId]);
                    mixpanelTrack("destination removed", {
                      "selected destination name": place.label,
                      "selected destination place id": place.placeId,
                    });
                  }}
                >
                  <ClearIcon />
                </PlainButton>
              </Tag>
            ))}
          </Taglist>

          {Boolean(talkingHead?.show) && (
            <TalkingHead show={talkingHead.show}>
              {talkingHead.text}
            </TalkingHead>
          )}
          {Boolean(invalidDestinations?.length) && (
            <DestinationErrorMessage>
              {t("invalidLocations", {
                locations: listFormatter(
                  invalidDestinations.map((d) => d.label),
                ),
              })}
            </DestinationErrorMessage>
          )}
        </Form>

        {getFeatureVariation("mediaProof") === "active" && (
          <MediaBar
            images={[
              MarketWatchSvg,
              ForbesSvg,
              CnnTravelSvg,
              UsaTodaySvg,
              WallStreetJournalSvg,
              TechCrunchSvg,
              TravelWeeklySvg,
            ]}
          />
        )}
      </>
    )
  );
}
